/// <reference types="google.maps" />

export const MAPS_SCRIPT_ID = 'google-maps-places';
const MAPS_ORIGIN = 'https://maps.googleapis.com/maps/api/js';
/** The global Google calls once its script is ready. */
export const MAPS_READY_CALLBACK = '__dormotechMapsReady';
const LOAD_TIMEOUT_MS = 10_000;

export interface MapsWindow extends Window {
  google?: typeof google;
  /** Google calls this when it refuses the key: a wrong key, or a site the key is not allowed on. */
  gm_authFailure?: () => void;
  [MAPS_READY_CALLBACK]?: () => void;
}

/**
 * Loads Google's Maps JavaScript API with its Places library — once, on the first
 * call, never at page load. Every later call shares that load. It rejects when
 * the script cannot be fetched, takes too long, or Google refuses the key, and
 * then stays rejected: nothing is loaded again until the page is reloaded.
 *
 * `onRefused` is told when Google refuses the key, which can also happen later,
 * on a search.
 */
export function createPlacesLoader(
  win: MapsWindow,
  apiKey: string,
  onRefused: () => void,
  timeoutMs = LOAD_TIMEOUT_MS,
): () => Promise<google.maps.PlacesLibrary> {
  let loading: Promise<google.maps.PlacesLibrary> | undefined;

  const start = (): Promise<google.maps.PlacesLibrary> =>
    new Promise<google.maps.PlacesLibrary>((resolve, reject) => {
      const previous = win.gm_authFailure;
      win.gm_authFailure = () => {
        onRefused();
        reject(new Error('Google refused the Maps key'));
        previous?.();
      };

      const ready = () => {
        win.google!.maps.importLibrary('places').then((library) => resolve(library as google.maps.PlacesLibrary), reject);
      };
      // Already on the page — loaded by something else, or by an earlier page of this app.
      if (win.google?.maps?.importLibrary) {
        ready();
        return;
      }

      const timer = win.setTimeout(() => reject(new Error('Google Maps took too long to load')), timeoutMs);
      win[MAPS_READY_CALLBACK] = () => {
        win.clearTimeout(timer);
        ready();
      };

      const doc = win.document;
      const script = doc.createElement('script');
      script.id = MAPS_SCRIPT_ID;
      script.async = true;
      script.src =
        `${MAPS_ORIGIN}?key=${encodeURIComponent(apiKey)}` +
        `&v=quarterly&loading=async&libraries=places&callback=${MAPS_READY_CALLBACK}`;
      // Blocked by an extension or a content policy, or no route to Google.
      script.onerror = () => {
        win.clearTimeout(timer);
        reject(new Error('Google Maps could not be loaded'));
      };
      (doc.head || doc.body || doc.documentElement).appendChild(script);
    });

  return () => {
    loading ??= start();
    return loading;
  };
}
